import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const ADMIN_TOKEN_KEY = 'admin_token';
export const ADMIN_AUTH_CHANGED_EVENT = 'admin-auth-changed';

const AdminAuthContext = createContext();

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);

export const notifyAdminAuthChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ADMIN_AUTH_CHANGED_EVENT));
  }
};

export const clearAdminToken = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  notifyAdminAuthChanged();
};

export const setAdminToken = (token) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  notifyAdminAuthChanged();
};

export const AdminAuthProvider = ({ children }) => {
  const [adminToken, setAdminTokenState] = useState(() => getAdminToken());
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(Boolean(getAdminToken()));
  const [loading, setLoading] = useState(true);

  const validateAdminToken = useCallback(async (token) => {
    if (!token) {
      return false;
    }

    try {
      await axios.get('/api/v1/doctors', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const syncFromStorage = useCallback(() => {
    const nextToken = getAdminToken();
    setAdminTokenState(nextToken);
    setIsAdminAuthenticated(Boolean(nextToken));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkAdminAuth = async () => {
      const token = getAdminToken();
      if (!token) {
        if (isMounted) {
          setAdminTokenState(null);
          setIsAdminAuthenticated(false);
          setLoading(false);
        }
        return;
      }

      const isValid = await validateAdminToken(token);
      if (!isMounted) {
        return;
      }

      if (isValid) {
        setAdminTokenState(token);
        setIsAdminAuthenticated(true);
      } else {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAdminTokenState(null);
        setIsAdminAuthenticated(false);
      }
      setLoading(false);
    };

    checkAdminAuth();

    return () => {
      isMounted = false;
    };
  }, [validateAdminToken]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === ADMIN_TOKEN_KEY) {
        syncFromStorage();
      }
    };

    window.addEventListener(ADMIN_AUTH_CHANGED_EVENT, syncFromStorage);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(ADMIN_AUTH_CHANGED_EVENT, syncFromStorage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [syncFromStorage]);

  const loginAdmin = useCallback(async (token) => {
    const trimmedToken = token.trim();
    const isValid = await validateAdminToken(trimmedToken);
    if (!isValid) {
      throw new Error('Invalid admin token');
    }

    setAdminToken(trimmedToken);
    setAdminTokenState(trimmedToken);
    setIsAdminAuthenticated(true);
    return trimmedToken;
  }, [validateAdminToken]);

  const logoutAdmin = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminTokenState(null);
    setIsAdminAuthenticated(false);
    notifyAdminAuthChanged();
  }, []);

  const value = {
    adminToken,
    isAdminAuthenticated,
    loading,
    loginAdmin,
    logoutAdmin,
    validateAdminToken
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => useContext(AdminAuthContext);
